
import * as React from "react";
import { cn } from "@/lib/utils";
import { DeltaPill, series, type SeriesSlot } from "./core";
import { Sparkline } from "./line-charts";

const HATCH_BG = "repeating-linear-gradient(135deg, var(--ads-chart-hatch) 0 5px, var(--ads-line) 5px 7px)";

/* ── Progress rows: solid share + hatched remainder ────────────────────── */

export function ProgressRows({
  rows,
  slot = 1,
}: {
  rows: { label: string; pct: number; value?: string }[];
  slot?: SeriesSlot;
}) {
  return (
    <ul className="flex flex-col gap-4">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate font-medium text-ink">{r.label}</span>
            <span className="shrink-0 text-xs tabular-nums text-muted">
              <span className="font-semibold text-text">{r.pct}%</span>
              {r.value && <> • {r.value}</>}
            </span>
          </div>
          <div
            role="meter"
            aria-label={r.label}
            aria-valuenow={r.pct}
            aria-valuemin={0}
            aria-valuemax={100}
            className="flex h-2.5 overflow-hidden rounded-full"
            style={{ background: HATCH_BG }}
          >
            <div className="h-full rounded-full" style={{ width: `${Math.max(2, r.pct)}%`, background: series(slot) }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ── Segmented breakdown bar + legend rows ─────────────────────────────── */

/**
 * One datum of a `SegmentedBreakdown`. `pct` is the datum's share (0–1) when
 * the caller already knows it: a caller whose own denominator differs from the
 * sum of the drawn values — the AUM tile draws one type against a wallet total,
 * not against the other types — must pass it, or the segment would be sized and
 * labelled against the wrong total. `fill` overrides the chart slot, so a
 * breakdown whose entities own a data colour (a wallet type's `--ads-viz-*`)
 * keeps it in the bar and the legend alike.
 */
export interface SegmentedBreakdownDatum {
  label: string;
  value: number;
  slot: SeriesSlot;
  pct?: number;
  fill?: string;
}

export function SegmentedBreakdown({
  data,
  format,
}: {
  data: SegmentedBreakdownDatum[];
  format?: (v: number) => string;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const share = (d: SegmentedBreakdownDatum) => d.pct ?? (total > 0 ? d.value / total : 0);
  // The segment is sized by its share, rounded to the tenth the legend prints,
  // so a 0% share keeps a zero-width segment (its `min-w` sliver) instead of a
  // bar the label does not claim, and the two never disagree.
  const width = (d: SegmentedBreakdownDatum) => `${Math.round(share(d) * 1000) / 10}%`;
  const shareText = (d: SegmentedBreakdownDatum) => (share(d) * 100).toFixed(1);
  const fill = (d: SegmentedBreakdownDatum) => d.fill ?? series(d.slot);
  const [hover, setHover] = React.useState<string | null>(null);
  return (
    <div>
      <div className="flex h-12 w-full gap-1.5" role="img" aria-label={data.map((d) => `${d.label} ${shareText(d)}%`).join(", ")}>
        {data.map((d) => (
          <div
            key={d.label}
            onPointerEnter={() => setHover(d.label)}
            onPointerLeave={() => setHover(null)}
            title={`${d.label}: ${format ? format(d.value) : d.value}`}
            className={cn("h-full min-w-[10px] rounded-[10px] transition-opacity", hover && hover !== d.label && "opacity-40")}
            style={{
              width: width(d),
              backgroundImage: `repeating-linear-gradient(135deg, color-mix(in srgb, var(--ads-surface) 28%, transparent) 0 3px, transparent 3px 9px), linear-gradient(180deg, color-mix(in oklab, ${fill(d)} 80%, white), ${fill(d)})`,
            }}
          />
        ))}
      </div>
      <ul className="mt-4 flex flex-col gap-2.5">
        {data.map((d) => (
          <li
            key={d.label}
            onPointerEnter={() => setHover(d.label)}
            onPointerLeave={() => setHover(null)}
            className={cn("flex items-center justify-between gap-3 text-sm transition-opacity", hover && hover !== d.label && "opacity-50")}
          >
            <span className="flex min-w-0 items-center gap-2 text-text">
              <span aria-hidden className="size-3 shrink-0 rounded-[4px]" style={{ background: fill(d) }} />
              <span className="truncate">{d.label}</span>
            </span>
            <span className="shrink-0 tabular-nums">
              {format && <span className="mr-2 text-xs text-muted">{format(d.value)}</span>}
              <span className="font-semibold text-ink">{shareText(d)}%</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Pill segments meter (e.g. 6 of 8 programs active) ─────────────────── */

/** Status colours (var(--ads-success) etc.) — states never borrow series hues. */
export function PillSegments({
  segments,
  label,
}: {
  segments: { key: string; color: string | "empty"; title: string }[];
  label: string;
}) {
  return (
    <div role="img" aria-label={label} className="flex w-full gap-1.5">
      {segments.map((s) => (
        <span
          key={s.key}
          title={s.title}
          className="h-2.5 flex-1 rounded-full"
          style={{ background: s.color === "empty" ? "var(--ads-disabled-soft)" : s.color }}
        />
      ))}
    </div>
  );
}

/* ── KPI card with sparkline ───────────────────────────────────────────── */

export function KpiSparkCard({
  label,
  icon,
  value,
  delta,
  hint,
  spark,
  slot = 3,
  sparkPlacement = "right",
  footer,
  className,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string;
  delta?: { direction: "up" | "down"; pct: number; good?: boolean };
  hint?: string;
  spark?: number[];
  slot?: SeriesSlot;
  /** "bottom" gives a full-width, taller sparkline — for tall, stretched cards. */
  sparkPlacement?: "right" | "bottom";
  footer?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-3 rounded-[16px] border border-line bg-surface p-4", sparkPlacement === "right" && "justify-between", className)}>
      <div className="flex items-center gap-2 text-[13px] font-medium text-muted">
        {icon && <span aria-hidden className="text-primary [&>svg]:size-4">{icon}</span>}
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[26px] font-semibold leading-none tracking-tight tabular-nums text-ink">{value}</div>
          <div className="mt-2 min-h-5">
            {delta ? <DeltaPill direction={delta.direction} pct={delta.pct} good={delta.good} suffix={hint} /> : hint && <span className="text-xs text-muted">{hint}</span>}
          </div>
        </div>
        {spark && sparkPlacement === "right" && (
          <div className="w-[42%] max-w-[140px] shrink-0">
            <Sparkline values={spark} slot={slot} ariaLabel={`${label} trend`} />
          </div>
        )}
      </div>
      {spark && sparkPlacement === "bottom" && (
        <div className="mt-auto pt-1">
          <Sparkline values={spark} slot={slot} height={64} ariaLabel={`${label} trend`} />
        </div>
      )}
      {footer}
    </div>
  );
}
